<script lang="ts" setup>
import { markRaw, onMounted, ref, watch } from 'vue';
import { useEmployeeStore } from '@/stores/employee';
import { useShiftStore } from '@/stores/shift';
import { storeToRefs } from 'pinia';
import { ElMessageBox, ElNotification } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { notify } from '@/utils/notification';
import { ErrorOutlinedIcon } from '@xpos/icons-svg';
import { useSectionStore } from '@/stores/section';
import { ISectionItem, ShiftStatus,IReportRequest } from '@posx/core';
import { useRouter } from 'vue-router';
import { useReportStore } from '@/stores/report';
import { logInfo } from '@/utils/logger';
import { runHealthCheck } from '@/utils/health.check';
import { useConfigStore } from '@/stores/config';
import { formatCurrency } from '@/utils/formatNumber';
import { cleanupSessionData } from '@/utils/cleanup';

const employeeStore = useEmployeeStore();
const shiftStore = useShiftStore();
const configStore = useConfigStore();
shiftStore.startShiftLiveQuery();
const sectionStore = useSectionStore();
const reportStore = useReportStore();
const { currentShift } = storeToRefs(shiftStore);
const { currentStaff } = storeToRefs(employeeStore);
const router = useRouter();
const { t } = useI18n();
const $emit = defineEmits(['change', 'update:modelValue', 'close']);
const $props = defineProps({
  modelValue: {
    type: Boolean,
  },
});

const visible = ref($props.modelValue);
const content = ref('');
const priceString = ref('');

watch(
  () => $props.modelValue,
  (newVal) => {
    visible.value = newVal;
  },
);

const handleClose = () => {
  $emit('change', false);
  $emit('update:modelValue', false);
  $emit('close', false);
  priceString.value = '';
  content.value = '';
};

const handleClick = async () => {
  if (!priceString.value) {
    return notify.error(t('shift.priceError'));
  }
  if (!currentShift.value) {
    await shiftStore.openShift(parseFloat(priceString.value), currentStaff.value?.uid!, content.value);
    runHealthCheck(); // fire-and-forget health check on shift open
    ElNotification({
      customClass: 'x-notification__success',
      title: t('common.success'),
      message: t('shift.shiftStarted'),
      type: 'success',
    });
    handleClose();
  } else {
    // Check config: only validate incomplete tables if end_shift_with_open_tables is false
    if (!configStore.appConfig?.config?.order?.end_shift_with_open_tables) {
      let incomplete = false;
      const subSectionResult = await sectionStore.getSectionItemList();
      if (subSectionResult && subSectionResult.list) {
        incomplete = !!subSectionResult.list.find((item: ISectionItem) => !!item.invoice_uid);
      }
      if (incomplete) return notify.error(t('shift.incomplete'));
    }
    ElMessageBox.confirm(t('shift.deleteConfirm.title'), 'Error', {
      confirmButtonText: t('shift.deleteConfirm.ok'),
      cancelButtonText: t('shift.deleteConfirm.cancel'),
      type: 'error',
      icon: markRaw(ErrorOutlinedIcon),
      autofocus: false,
    }).then(async () => {
      try {

        // Remove all debug_ prefixed localStorage items
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('debug_')) localStorage.removeItem(key);
        });

        const service = await shiftStore.getService();
        const shift = await service.getOneByParams({ status: ShiftStatus.Open });
        const printReportRequest: IReportRequest = {
          report_code_name: 'shift_report',
          start_datetime: shift?.started_at ?? new Date(),
          end_datetime: new Date(),
          copies: 1,
          shift_uid: shift?.uid ?? '',
          declared_amount: parseFloat(priceString.value),
        };
        logInfo('ENDING SHIFT', { shift });
        logInfo('PRINT SHIFT REPORT REQUEST', printReportRequest);
        await reportStore.printReport(printReportRequest);
        await shiftStore.closeShift(parseFloat(priceString.value), currentStaff.value?.uid!);
        ElNotification({
          customClass: 'x-notification__success',
          title: t('common.success'),
          message: t('shift.shiftEnded'),
          type: 'success',
        });
        // Optional: logout current staff after end-shift for shared-device scenarios
        if (configStore.appConfig?.config?.order?.logout_after_end_shift) {
          await employeeStore.logout();
          await cleanupSessionData();
          router.push('/login');
        }
        handleClose();
      } catch (e) {
        notify.error((e as Error).message);
      }
    }).catch(() => {});
  }
};

onMounted(() => shiftStore.getShiftStatus()); // load the open shift into currentShift
</script>

<template>
  <x-dialog v-model="visible" width="770px"
    :title="!shiftStore.currentShift ? t('shift.title.start') : t('shift.title.end')" :before-close="handleClose"
    @close="handleClose" :close-on-click-modal="false">
    <div class="w-[350px] flex flex-col justify-between">
      <div>
        <!-- <x-input
          :label="!startShift ? t('shift.label.openAmount') : t('shift.label.declareAmount')"
          v-model="priceString"
          type="text"
          class="mb-[10px]"
        /> -->
        <x-item :border="false" class="mb-[10px] text-xl bg-[#ececfe]">
          {{ t('shift.label.currentBalance') }}
          <template #end>
            <span class="whitespace-nowrap">{{ formatCurrency(currentShift?.drawer_amount ?? 0) }}</span>
          </template>
        </x-item>
        <x-item :border="true" class="mb-[10px] text-xl border-2 !border-[#535998]">
          {{ !shiftStore.currentShift ? t('shift.label.openAmount') : t('shift.label.declareAmount') }}
          <template #end>
            {{ priceString }}
          </template>
        </x-item>
        <el-input type="textarea" v-model="content" :rows="3" resize="none" :placeholder="t('shift.label.remark')"
          style="--el-input-border-color: #a8a8a8; --el-font-size-base: 20px" class="mb-[10px]" />
      </div>
      <div>
        <x-keyboard v-model="priceString" />
        <x-button type="primary" class="w-full mt-1" :debounceTime=5000 @click="handleClick">{{
          !shiftStore.currentShift ? t('shift.button.startShift') : t('shift.button.endShift') }}</x-button>
      </div>
    </div>
  </x-dialog>
</template>
